import React, { useState } from 'react';
import { PipelineBuilderProvider, usePipelineBuilder } from '../context/PipelineBuilderContext';
import { PipelinePalette } from '../components/pipelineBuilder/PipelinePalette';
import { PipelineCanvas } from '../components/pipelineBuilder/PipelineCanvas';
import { PipelineNodeConfigPanel } from '../components/pipelineBuilder/PipelineNodeConfigPanel';
import PipelineBrowser from '../components/pipelineBuilder/PipelineBrowser';
import { pipelineService } from '../services/pipelineService';
import '../styles/dashboard-pages.css';
import '../styles/pipelineBuilder.css';

const PipelineBuilderContent: React.FC = () => {
  const {
    pipeline,
    createNewPipeline,
    nodes,
    selectedNodeId,
    selectNode,
    getPipelineJSON,
    reset,
    undo,
    redo,
    history,
    historyIndex,
  } = usePipelineBuilder();

  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedNode = nodes.find(n => n.id === selectedNodeId) || null;

  const handleNewPipeline = () => {
    const name = prompt('Enter pipeline name:', 'My Pipeline');
    if (name) {
      createNewPipeline(name);
    }
  };

  const handleExportJSON = () => {
    if (!pipeline) {
      alert('No pipeline to export');
      return;
    }

    const json = getPipelineJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pipeline.name.replace(/\s+/g, '_')}_${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSavePipeline = async () => {
    if (!pipeline || nodes.length === 0) {
      alert('No pipeline to save. Please create a pipeline first.');
      return;
    }

    const pipelineName = prompt('Enter pipeline name:', pipeline.name || 'My Pipeline');
    if (!pipelineName) {
      return;
    }

    const description = prompt('Enter pipeline description (optional):', '') || undefined;

    setIsSaving(true);
    try {
      const pipelineJson = getPipelineJSON();
      const pipelineObj = JSON.parse(pipelineJson);

      await pipelineService.savePipeline({
        name: pipelineName,
        pipeline: pipelineObj,
        description,
      });

      alert('Pipeline saved successfully!');
    } catch (error: any) {
      alert(`Failed to save pipeline: ${error.message || 'Unknown error'}`);
      console.error('Error saving pipeline:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadPipeline = () => {
    setIsBrowserOpen(true);
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="header-content">
          <h1>⚙️ Pipeline Builder</h1>
          <p>Create data processing pipelines by connecting models, APIs, and logic blocks</p>
        </div>
      </div>

      <div className="pipeline-builder-container">
        {/* Left Sidebar - Node Palette */}
        <aside className="builder-sidebar">
          <PipelinePalette />
        </aside>

        {/* Main Canvas */}
        <main className="builder-main">
          <div className="builder-toolbar">
            <div className="toolbar-left">
              <button className="btn-primary" onClick={handleNewPipeline}>
                ➕ New Pipeline
              </button>
              {pipeline && (
                <span className="arch-name">{pipeline.name}</span>
              )}
            </div>

            <div className="toolbar-center">
              <button
                className="btn-icon"
                onClick={undo}
                disabled={historyIndex <= 0}
                title="Undo"
              >
                ↶
              </button>
              <button
                className="btn-icon"
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                title="Redo"
              >
                ↷
              </button>
            </div>

            <div className="toolbar-right">
              <button className="btn-secondary" onClick={reset}>
                🔄 Reset
              </button>
              <button className="btn-secondary" onClick={handleLoadPipeline}>
                📂 Load Pipeline
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSavePipeline}
                disabled={isSaving || !pipeline || nodes.length === 0}
                title={!pipeline || nodes.length === 0 ? 'Create a pipeline first' : 'Save pipeline to storage'}
              >
                {isSaving ? '💾 Saving...' : '💾 Save Pipeline'}
              </button>
              <button className="btn-primary" onClick={handleExportJSON}>
                📥 Export JSON
              </button>
            </div>
          </div>

          <PipelineCanvas />
        </main>

        {/* Right Sidebar - Properties Panel */}
        <aside className="builder-properties">
          <PipelineNodeConfigPanel node={selectedNode} />
        </aside>
      </div>

      {/* Pipeline Browser Modal */}
      <PipelineBrowser 
        isOpen={isBrowserOpen} 
        onClose={() => setIsBrowserOpen(false)} 
      />
    </div>
  );
};

const PipelineBuilderPage: React.FC = () => {
  return (
    <PipelineBuilderProvider>
      <PipelineBuilderContent />
    </PipelineBuilderProvider>
  );
};

export default PipelineBuilderPage;
